import React, { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout/AuthLayout";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import NewEmailIllustration from "../../icons/new-email-illustration.svg";
import styles from "./Login.module.scss";
import axiosInstance from "../../utils/utils";
import { useDispatch } from "react-redux";
import { showModal } from "../../redux/modalStore";
import { useLocation } from "react-router-dom";

const RESEND_COOLDOWN_MS = 60 * 1000; //60s

const Login = () => {
  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
  });

  const dispatch = useDispatch();
  const location = useLocation();

  // Load persisted state (survives refresh)
  const [emailSent, setEmailSent] = useState(() => {
    const flag = localStorage.getItem("login_email_sent");
    return flag === "true";
  });
  const [lastEmail, setLastEmail] = useState(
    () => localStorage.getItem("login_email") || "",
  );
  const [emailSentAt, setEmailSentAt] = useState(() => {
    const ts = Number(localStorage.getItem("login_sent_at") || 0);
    return Number.isFinite(ts) ? ts : 0;
  });

  // Cooldown countdown (seconds)
  const [cooldownLeft, setCooldownLeft] = useState(0);
  useEffect(() => {
    if (!emailSentAt) return;
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - emailSentAt)) / 1000),
      );
      setCooldownLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [emailSentAt]);

  const canResend = emailSent && !!lastEmail && cooldownLeft === 0;

  const handleSubmit = (values, actions) => {
    const options = {
      method: "POST",
      url: "/auth/session",
      params: {
        email: values.email,
      },
    };

    return axiosInstance
      .request(options)
      .then(() => {
        const now = Date.now();
        setLastEmail(values.email);
        setEmailSent(true);
        setEmailSentAt(now);
        localStorage.setItem("login_email", values.email);
        localStorage.setItem("login_email_sent", "true");
        localStorage.setItem("login_sent_at", String(now));
      })
      .catch((error) => {
        console.error("Login error:", error);
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      const now = Date.now();
      await axiosInstance.request({
        method: "POST",
        url: "/auth/session",
        params: { email: lastEmail },
      });
      setEmailSentAt(now);
      localStorage.setItem("login_sent_at", String(now));
    } catch (error) {
      console.error("Resend error:", error);
      dispatch(showModal({ name: "error_modal" }));
    }
  };

  const formatSeconds = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const showExpiredBanner = location.state?.expired;

  return (
    <AuthLayout>
      {!emailSent && (
        <Formik
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, isValid }) => (
            <Form>
              {showExpiredBanner && (
                <div className={styles.noticeError}>
                  Your login link has expired. Request a new one below.
                </div>
              )}
              <div>
                <Field
                  name="email"
                  type="text"
                  component={Input}
                  placeholder="Enter your email address here"
                  className={styles.input}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting || !values.email || !isValid}
                text={isSubmitting ? "Sending..." : "Login"}
                className={styles.button}
              />
            </Form>
          )}
        </Formik>
      )}
      {emailSent && (
        <div className={styles.wrapper}>
          <img src={NewEmailIllustration} alt="Illustration of new email" />
          <span className={styles.text}>
            We’ve sent a login link to <b>{lastEmail}</b> <br />
            Check your inbox (and Spam/Promotions) to hop right in!
            <br />
            <br />
            <span className={styles.blueText}>
              Please note: the link is valid for <b>10 minutes</b>.
            </span>
          </span>

          <div className={styles.resendRow}>
            <Button
              onClick={handleResend}
              disabled={!canResend}
              text={
                canResend
                  ? "Resend link"
                  : `Resend in ${formatSeconds(cooldownLeft)}`
              }
              className={styles.buttonResend}
            />
            <button
              type="button"
              className={styles.changeEmail}
              onClick={() => {
                setEmailSent(false);
                localStorage.setItem("login_email_sent", "false");
              }}
            >
              Use a different email
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
